import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { TEXT_COLOR_OPTIONS } from "../../components/Helper/JobsiteCommand";
import FlipGroup from "./FlipGroup";

const MIN_FONT_SIZE = 14;
const MAX_FONT_SIZE = 70;

interface MilestoneCountdownProps {
    milestone: {
        title?: string;
        end_at?: string | null;
        color?: string;
    };
}

interface TimeRemaining {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

// Binary search for the largest font size whose content still fits within `box`'s current
// width. `box` and `content` are separate elements (a bounding box and the text/tiles inside
// it) so the box's width comes from layout (a flex share of the card), not from the content
// we're trying to size - otherwise shrinking content and shrinking box chase each other.
const fitWidth = (box: HTMLDivElement | null, content: HTMLDivElement | null): number | null => {
    if (!box || !content) {
        return null;
    }

    const boxWidth = box.clientWidth;
    if (boxWidth === 0) {
        return null;
    }

    const fits = (size: number) => {
        content.style.fontSize = `${size}px`;
        return content.scrollWidth <= boxWidth;
    };

    let low = MIN_FONT_SIZE;
    let high = MAX_FONT_SIZE;
    let best = MIN_FONT_SIZE;

    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        if (fits(mid)) {
            best = mid;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    content.style.fontSize = `${best}px`;
    return best;
};

const MilestoneCountdown: React.FC<MilestoneCountdownProps> = (props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const titleBoxRef = useRef<HTMLDivElement>(null);
    const titleTextRef = useRef<HTMLDivElement>(null);
    const digitsBoxRef = useRef<HTMLDivElement>(null);
    const digitsContentRef = useRef<HTMLDivElement>(null);

    const [titleFontSize, setTitleFontSize] = useState(MIN_FONT_SIZE);
    const [digitsFontSize, setDigitsFontSize] = useState(MIN_FONT_SIZE);

    const getTimeRemaining = (endAt: string | null | undefined): TimeRemaining => {
        if (!endAt) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        const endMs = new Date(endAt).getTime();
        if (Number.isNaN(endMs)) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        const diffMs = endMs - Date.now();
        const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));

        return {
            days: Math.floor(totalSeconds / 86400),
            hours: Math.floor((totalSeconds % 86400) / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
            seconds: totalSeconds % 60,
        };
    };
    const [remaining, setRemaining] = useState<TimeRemaining>(() => getTimeRemaining(props.milestone.end_at));

    useEffect(() => {
        setRemaining(getTimeRemaining(props.milestone.end_at));

        const timer = setInterval(() => {
            setRemaining(getTimeRemaining(props.milestone.end_at));
        }, 1000);

        return () => clearInterval(timer);
    }, [props.milestone.end_at]);

    const color = props.milestone.color || TEXT_COLOR_OPTIONS[0].value;

    const pad = (value: number, length: number = 2): string => value.toString().padStart(length, "0");

    // The title and digits each get a fixed share of the card's width (set in the flex row
    // below), then fit independently within their own share - sized off the card itself, not
    // the viewport, so this scales correctly no matter how the Command Center grid divides up
    // the screen.
    const fitAll = useCallback(() => {
        const title = fitWidth(titleBoxRef.current, titleTextRef.current);
        if (title !== null) {
            setTitleFontSize(title);
        }

        const digits = fitWidth(digitsBoxRef.current, digitsContentRef.current);
        if (digits !== null) {
            setDigitsFontSize(digits);
        }
    }, [props.milestone.title]);

    useLayoutEffect(() => {
        fitAll();
    }, [fitAll]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const observer = new ResizeObserver(() => fitAll());
        observer.observe(container);

        return () => observer.disconnect();
    }, [fitAll]);

    return (
        <div
            ref={containerRef}
            className="w-full rounded-lg px-6 py-3 overflow-hidden"
            style={{ borderColor: color, backgroundColor: `${color}50` }}
        >
            <div className="flex flex-row items-center justify-between gap-4">
                <div ref={titleBoxRef} className="flex-none w-[38%] overflow-hidden">
                    <div
                        ref={titleTextRef}
                        className="font-plot-oswald uppercase text-white"
                        style={{ fontSize: titleFontSize }}
                    >
                        {props.milestone.title || "Untitled Milestone"}
                    </div>
                </div>

                <div ref={digitsBoxRef} className="flex-1 min-w-0 overflow-hidden flex justify-end">
                    <div
                        ref={digitsContentRef}
                        className="flex flex-row items-end gap-2"
                        style={{ fontSize: digitsFontSize }}
                    >
                        <FlipGroup value={pad(remaining.days, 3)} label="days" color={color} />
                        <div className="text-[0.85em] font-plot-oswald uppercase">:</div>
                        <FlipGroup value={pad(remaining.hours)} label="hrs" color={color} />
                        <div className="text-[0.85em] font-plot-oswald uppercase">:</div>
                        <FlipGroup value={pad(remaining.minutes)} label="mins" color={color} />
                        <div className="text-[0.85em] font-plot-oswald uppercase">:</div>
                        <FlipGroup value={pad(remaining.seconds)} label="secs" color={color} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MilestoneCountdown;
